import { useCallback, useMemo } from 'react';
// import { router } from 'expo-router';
// import { Linking } from 'react-native';
import { useGetBuildingTooltipQuery } from '@cobu/api-client';
import { TOOLTIP_POLL_MS } from '@cobu/api-client';
import { BuildingTooltip } from '@cobu/types';
// import { useIsFocused } from '@react-navigation/native';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { showErrorMessage } from '../utils/messageToast';
import { toTitleCase } from '../utils/string';
import { Strings } from '../constants';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';
import { useFeatureFlags } from './useFeatureFlags';
import { useResidentRestrictions } from './useResidentRestrictions';

interface UseChatTooltipOptions {
  buildingId: string;
  enabled?: boolean;
}

interface UseChatTooltipReturn {
  tooltip: BuildingTooltip | null;
  isLoading: boolean;
  error: string | null;
  handleTooltipCtaPress: (tooltip: BuildingTooltip) => void;
}

export const useChatTooltip = ({
  buildingId,
  enabled = true,
}: UseChatTooltipOptions): UseChatTooltipReturn => {
  const navigate = useNavigate();
  const strings = Strings.get();
  const { user } = useSelector((state: RootState) => state.auth);
  const isFocused = true // useIsFocused();
  const tooltipPollingEnabled = useSelector((state: RootState) => state.settings.tooltipPollingEnabled);
  const { residentsCanCreateEvents, showGroups } = useFeatureFlags();
  const { canCreateEvent } = useResidentRestrictions();

  // Fetch building tooltip with cache and refresh
  const tooltipQuery = useGetBuildingTooltipQuery(buildingId || '', {
    enabled: enabled && !!buildingId && !!isFocused,
    refetchInterval: enabled && !!buildingId && !!isFocused && tooltipPollingEnabled ? TOOLTIP_POLL_MS : false,
  });

  // Extract tooltip data (first item from array, if available)
  const tooltip = useMemo(() => {
    return tooltipQuery.data?.[0] || null;
  }, [tooltipQuery.data]);

  // Handle tooltip CTA press with navigation logic from old app
  const handleTooltipCtaPress = useCallback(
    async (tooltip: BuildingTooltip) => {
      if (!tooltip.linkType) {
        logger.warn('Tooltip CTA pressed but no linkType provided');
        return;
      }

      try {

        analyticsService.trackEvent(AnalyticsEvent.BUILDING_TOOLTIP_CTA_PRESSED, {
          destination: tooltip.linkType,
          source: 'chat_tab',
          title: tooltip.title,
          tooltipId: tooltip.uuid,
          linkUrl: tooltip.linkURL,
        });

        // Handle navigation based on linkType (matching old app logic)
        switch (tooltip.linkType) {
          case 'Referral':
            // Navigate to perks tab with referral detail
            analyticsService.trackEvent(AnalyticsEvent.REFERRAL_HOME_CTA_CLICK, {
              buildingId: user?.building?.uuid,
              buildingName: user?.building?.name,
              userId: user?.uuid,
            });
            navigate('/perks/referral');
            break;

          case 'Events':
            navigate('/events');
            break;

          case 'Groups':
            if (showGroups) {
              if (tooltip.params?.groupId) {
                navigate(`/groups/${tooltip.params.groupId}`);
              } else {
                navigate('/groups');
              }
            }
            break;

          case 'Perks':
            navigate('/perks');
            break;

          case 'Marketplace':
            navigate('/marketplace');
            break;

          case 'Chat':
            // Already on chat tab, could scroll to top or refresh
            break;

          case 'WebView':
            if (tooltip.link || tooltip.linkURL) {
              const url = tooltip.link || tooltip.linkURL || '';
              navigate('/webview', {
                state: {
                  url,
                  title: toTitleCase(tooltip.title || ''),
                }
              });
            }
            break;

          case 'CreateGroupEvent':
            if (residentsCanCreateEvents && canCreateEvent) {
              navigate('/events/create-group-event');
            }
            break;

          case 'External':
            if (tooltip.linkURL) {
              if (tooltip.linkURL.includes('typeform')) {
                // Open Typeform in webview since building-survey route doesn't exist
                navigate('/webview', {
                  state: {
                    url: tooltip.linkURL,
                    title: toTitleCase(tooltip.title || strings.webView.surveyFallbackTitle),
                  }
                });
              } else if (tooltip.linkURL.startsWith("sms:") || tooltip.linkURL.startsWith("mailto:")) {
                window.open(tooltip.linkURL, '_blank');
              } else {
                let normalizedUrl = tooltip.linkURL;
                if (!normalizedUrl.startsWith("http://") && !normalizedUrl.startsWith("https://")) {
                  normalizedUrl = `https://${normalizedUrl}`;
                }
                window.open(normalizedUrl, '_blank');
              }
            }
            break;

          default:
            logger.warn(`Unknown tooltip linkType: ${tooltip.linkType}`);
            break;
        }
      } catch (error) {
        logger.error('Error handling tooltip CTA press:', error);
        showErrorMessage(strings.tooltip.errors.actionFailed);

        analyticsService.trackEvent(AnalyticsEvent.BUILDING_TOOLTIP_CTA_FAILED, {
          destination: tooltip.linkType,
          source: 'chat_tab',
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    },
    [navigate, strings.tooltip.errors.actionFailed, residentsCanCreateEvents, canCreateEvent, showGroups]
  );

  // Error handling
  const error = useMemo(() => {
    if (tooltipQuery.error) {
      logger.error('Failed to load building tooltip:', tooltipQuery.error);
      return strings.tooltip.errors.loadFailed;
    }
    return null;
  }, [tooltipQuery.error, strings.tooltip.errors.loadFailed]);

  return {
    tooltip,
    isLoading: tooltipQuery.isLoading,
    error,
    handleTooltipCtaPress,
  };
};

export default useChatTooltip; 